import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import EventSummaryCard from '../components/EventSummaryCard.jsx';
import PackageDetailsCard from '../components/PackageDetailsCard.jsx';
import AttendeeFormCard from '../components/AttendeeFormCard.jsx';
import TermsCard from '../components/TermsCard.jsx';
import api, { API_BASE_URL } from '../../../../config/api';
import axiosInstance from '../../../../utils/api/axiosInstance.js';
import { isValidEmail } from '../../../../utils/validationUtils';
import { getDisplayPrice, formatPrice, parseAmountToNumber } from '../../../../utils/priceUtils.js';
import '../../TicketDetails/css/TicketDetails.css';

const titles = ['Prof', 'Dr', 'Mr', 'Mrs', 'Ms', 'Miss', 'Prefer not to say'];
const genders = ['Male', 'Female', 'Other'];
const MAX_ATTENDEES = 100;

const CustomerTicketDetails = () => {
  const nav = useNavigate();
  const { eventId, packageIndex } = useParams();

  const [event, setEvent] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendeeTypes, setAttendeeTypes] = useState([]);
  const [delegates, setDelegates] = useState([
    { title: '', name: '', gender: '', email: '', phone: '', ieee: '', delegation: '' },
  ]);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFormModal, setShowFormModal] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('');

  // Check customer profile completeness
  const checkCustomerProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/shared/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      console.log('🔍 Customer profile check result:', result);
      
      if (result.success && result.data) {
        const profile = result.data;
        const isProfileIncomplete = !profile.firstname || !profile.surname || 
                                  !profile.cellnumber || profile.cellnumber === '0000000000';
        console.log('📊 Profile validation:', {
          firstname: profile.firstname,
          surname: profile.surname,
          cellnumber: profile.cellnumber,
          isProfileIncomplete
        });
        setProfileIncomplete(isProfileIncomplete);
      }
    } catch (error) {
      console.error('Error checking customer profile:', error);
    }
  };

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem('token');
        if (!token) {
          nav('/');
          return;
        }

        const response = await axiosInstance.get(`/shared/events/${eventId}`);
        const data = response.data;

        // Process date and time with friendly formatting
        let formattedDate = 'TBA';
        const rawDate = data.startdate || data.start_date || data.date;
        if (rawDate) {
          const parsed = new Date(rawDate);
          if (!Number.isNaN(parsed.getTime())) {
            formattedDate = parsed.toLocaleDateString('en-ZA', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
          } else {
            formattedDate = rawDate;
          }
        }

        const formattedTime = data.time || data.start_time || 'TBA';

        const eventWithFormatting = {
          ...data,
          formattedDate,
          formattedTime,
        };

        setEvent(eventWithFormatting);

        // Attendee types
        const defaultAttendeeTypes = ['Delegate', 'Speaker', 'Exhibitor', 'Visitor'];
        let processedAttendees = defaultAttendeeTypes;

        if (data.attendees) {
          try {
            if (typeof data.attendees === 'string') {
              const parsed = JSON.parse(data.attendees);
              if (Array.isArray(parsed)) {
                processedAttendees = parsed.filter((item) => typeof item === 'string' && item.trim());
              }
            } else if (Array.isArray(data.attendees)) {
              processedAttendees = data.attendees.filter((item) => typeof item === 'string' && item.trim());
            }
          } catch (e) {
            processedAttendees = defaultAttendeeTypes;
          }
        }

        if (!processedAttendees.length) {
          processedAttendees = defaultAttendeeTypes;
        }

        setAttendeeTypes(processedAttendees);
        setDelegates((prev) => [
          {
            ...prev[0],
            title: '',
            gender: '',
            delegation: processedAttendees[0],
          },
        ]);

        // Package
        if (data.packages && Array.isArray(data.packages) && data.packages[packageIndex]) {
          const pkg = data.packages[packageIndex];
          let parsedPackage;

          if (typeof pkg === 'string') {
            try {
              if (pkg.startsWith('{') && pkg.endsWith('}')) {
                const cleanedStr = pkg.replace(/\\\\|\\"/g, '"');
                parsedPackage = JSON.parse(cleanedStr);
              } else {
                const pricingMatch = pkg.match(/"pricing":"\\"([^\\]+)\\""/i);
                const typeMatch = pkg.match(/"selectType":"\\"([^\\]+)\\""/i);
                const detailsMatch = pkg.match(/"details":"\\"([^\\]+)\\""/i);

                parsedPackage = {
                  name: typeMatch ? typeMatch[1] : `Package ${parseInt(packageIndex, 10) + 1}`,
                  price: pricingMatch ? pricingMatch[1] : '0.00',
                  details: detailsMatch ? detailsMatch[1] : '',
                };
              }
            } catch (e) {
              parsedPackage = {
                name: `Package ${parseInt(packageIndex, 10) + 1}`,
                price: '0.00',
                details: '',
              };
            }
          } else {
            parsedPackage = pkg;
          }

          setPackageData(parsedPackage);
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError(err.message || 'Failed to load event.');
      } finally {
        setLoading(false);
        // Check customer profile after loading event details
        checkCustomerProfile();
      }
    };

    if (eventId && packageIndex != null) {
      fetchEventDetails();
    }
  }, [eventId, packageIndex, nav]);

  const handleAddRow = () => {
    if (delegates.length >= MAX_ATTENDEES) return;
    setDelegates([
      ...delegates,
      {
        title: '',
        name: '',
        gender: '',
        email: '',
        phone: '',
        ieee: '',
        delegation: attendeeTypes.length > 0 ? attendeeTypes[0] : 'Attendee',
      },
    ]);
  };

  const handleRemoveRow = () => {
    if (delegates.length > 1) {
      setDelegates(delegates.slice(0, -1));
    }
  };

  const handleRemoveDelegateAtIndex = (indexToRemove) => {
    if (delegates.length <= 1) return;
    const updatedDelegates = delegates.filter((_, idx) => idx !== indexToRemove);
    setDelegates(updatedDelegates);
  };

  const handleAttendeeCountChange = (rawValue) => {
    // Allow empty while typing, but don't change delegates until a number
    if (rawValue === '') {
      return;
    }

    let num = parseInt(rawValue, 10);

    if (Number.isNaN(num) || num < 1) {
      num = 1;
    }

    if (num > MAX_ATTENDEES) {
      num = MAX_ATTENDEES;
    }

    const current = delegates;
    if (num === current.length) return;

    // Increase number of delegates up to MAX_ATTENDEES
    if (num > current.length) {
      const template = current[0] || {
        title: '',
        name: '',
        gender: '',
        email: '',
        phone: '',
        ieee: '',
        delegation: attendeeTypes.length > 0 ? attendeeTypes[0] : 'Attendee',
      };

      const extras = [];
      for (let i = current.length; i < num; i += 1) {
        extras.push({
          ...template,
          name: '',
          email: '',
          phone: '',
          ieee: '',
        });
      }
      setDelegates([...current, ...extras]);
      return;
    }

    // Decrease number of delegates (drop extras)
    if (num < current.length) {
      setDelegates(current.slice(0, num));
    }
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...delegates];
    const newValidationErrors = { ...validationErrors };
    const errorKey = `${index}-${field}`;

    delete newValidationErrors[errorKey];

    if (field === 'name') {
      const nameRegex = /^[A-Za-z\s-]*$/;
      if (!nameRegex.test(value)) {
        newValidationErrors[errorKey] = 'Name can only contain letters, spaces, and hyphens';
        setValidationErrors(newValidationErrors);
        return;
      }
      if (!value || value.trim() === '') {
        newValidationErrors[errorKey] = 'Name is required';
      }
    } else if (field === 'email') {
      if (!value || value.trim() === '') {
        newValidationErrors[errorKey] = 'Email is required';
      } else if (!isValidEmail(value)) {
        newValidationErrors[errorKey] = 'Please enter a valid email address';
      }
    } else if (field === 'phone') {
      if (!value || value.trim() === '') {
        newValidationErrors[errorKey] = 'Phone number is required';
      } else {
        const phoneRegex = /^[0-9]*$/;
        if (!phoneRegex.test(value)) {
          newValidationErrors[errorKey] = 'Phone number can only contain digits';
          setValidationErrors(newValidationErrors);
          return;
        }
        if (value.length > 0 && value[0] !== '0') {
          value = '0' + value.substring(value.length > 1 ? 1 : 0);
        }
        if (value.length > 10) {
          value = value.substring(0, 10);
        }
        if (value.length > 0 && value.length < 10) {
          newValidationErrors[errorKey] = 'Phone number must be exactly 10 digits';
        }
      }
    } else if (field === 'ieee') {
      if (value && value.trim() !== '') {
        const ieeeRegex = /^[0-9]*$/;
        if (!ieeeRegex.test(value)) {
          newValidationErrors[errorKey] = 'IEEE number can only contain digits';
          setValidationErrors(newValidationErrors);
          return;
        }
        if (value.length > 8) {
          value = value.substring(0, 8);
        }
        if (value.length > 0 && value.length < 8) {
          newValidationErrors[errorKey] = 'IEEE number must be exactly 8 digits';
        }
      }
    }

    setValidationErrors(newValidationErrors);
    updated[index][field] = value;
    setDelegates(updated);
  };

  const handleSubmit = () => {
    let allErrors = {};
    let hasErrors = false;

    const validateDelegate = (delegate, index) => {
      const errors = {};
      if (!delegate.name || delegate.name.trim() === '') {
        errors[`${index}-name`] = 'Name is required';
      } else if (!/^[A-Za-z\s-]*$/.test(delegate.name)) {
        errors[`${index}-name`] = 'Name can only contain letters, spaces, and hyphens';
      }
      if (!delegate.email || delegate.email.trim() === '') {
        errors[`${index}-email`] = 'Email is required';
      } else {
        const emailRegex = /^[^\s@]+@(gmail\.com|outlook\.com|yahoo\.com|hotmail\.com|icloud\.com|.*\.edu|.*\.org|.*\.co\.za)$/i;
        if (!emailRegex.test(delegate.email)) {
          errors[`${index}-email`] = 'Please enter a valid email address with a common domain';
        }
      }
      if (!delegate.phone || delegate.phone.trim() === '') {
        errors[`${index}-phone`] = 'Phone number is required';
      } else if (!/^[0-9]*$/.test(delegate.phone)) {
        errors[`${index}-phone`] = 'Phone number can only contain digits';
      } else if (delegate.phone.length !== 10) {
        errors[`${index}-phone`] = 'Phone number must be exactly 10 digits';
      } else if (delegate.phone[0] !== '0') {
        errors[`${index}-phone`] = 'Phone number must start with 0';
      }
      if (delegate.ieee && delegate.ieee.trim() !== '') {
        if (!/^[0-9]*$/.test(delegate.ieee)) {
          errors[`${index}-ieee`] = 'IEEE number can only contain digits';
        } else if (delegate.ieee.length !== 8) {
          errors[`${index}-ieee`] = 'IEEE number must be exactly 8 digits';
        }
      }
      return errors;
    };

    delegates.forEach((delegate, index) => {
      const delegateErrors = validateDelegate(delegate, index);
      if (Object.keys(delegateErrors).length > 0) {
        allErrors = { ...allErrors, ...delegateErrors };
        hasErrors = true;
      }
    });

    setValidationErrors(allErrors);

    if (hasErrors) {
      return false; // Validation failed
    }

    const rawPrice = packageData?.price || packageData?.pricing || '0.00';
    const unitPrice = parseAmountToNumber(rawPrice);
    const displayPrice = getDisplayPrice(rawPrice);
    const formattedDisplayPrice = formatPrice(displayPrice);
    console.log('[CustomerTicketDetails handleSubmit] rawPrice:', rawPrice, 'displayPrice:', displayPrice, 'formatted:', formattedDisplayPrice);

    const formattedDelegates = delegates.map((delegate) => ({
      packageId: packageData?.package_id || packageData?.PackageID || null,
      packageDetails: packageData?.name || packageData?.selectType || 'Conference Delegate',
      title: delegate.title,
      name: delegate.name,
      gender: delegate.gender,
      email: delegate.email,
      phone: delegate.phone,
      delegation: delegate.delegation,
      tickets: 1,
      ieeeNumber: delegate.ieee,
      dayPass: packageData?.Duration || '',
      amount: formattedDisplayPrice,
      unitPrice,
    }));

    let existingPackages = [];
    try {
      const storedPackages = localStorage.getItem('currentPackages');
      if (storedPackages) {
        existingPackages = JSON.parse(storedPackages);
        localStorage.removeItem('currentPackages');
      }
    } catch (e) {
      console.error('Error parsing stored packages:', e);
    }

    const allDelegates = [...existingPackages, ...formattedDelegates];
    nav(`/confirm-ticket-package/${eventId}/${packageIndex}`, {
      state: { delegates: allDelegates },
    });
    return true; // Validation succeeded
  };

  const handleOpenForm = async () => {
    // Check profile completeness before allowing purchase
    const token = localStorage.getItem('token');
    if (!token) {
      nav('/');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/shared/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success && result.data) {
        const profile = result.data;
        const isProfileIncomplete = !profile.firstname || !profile.surname || 
                                  !profile.cellnumber || profile.cellnumber === '0000000000' || profile.cellnumber === '000000000';
        
        if (isProfileIncomplete) {
          setCustomerMessage('Please fill in all profile information required to purchase tickets.');
          setShowCustomerPopup(true);
        } else {
          // Profile is complete, allow purchase
          setShowFormModal(true);
        }
      }
    } catch (error) {
      console.error('Error checking customer access:', error);
      // On error, allow access but log the issue
      setShowFormModal(true);
    }
  };

  const handleCloseForm = () => {
    setShowFormModal(false);
  };

  return (
    <div className="ticket-details-page">
      <GlobalHeader
        title="Fill In Ticket Details"
        subtitle="Fill in attendee details for your selected package"
        backTo={`/customerviewevent/${eventId}`}
        logoutTo="/"
        profileIncomplete={profileIncomplete}
      />

      <main className="ticket-details-page__main">
        {loading ? (
          <div className="ticket-details-loading">
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <div>Loading event details...</div>
          </div>
        ) : error ? (
          <div className="ticket-details-error">
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⚠️</div>
            <div>Error: {error}</div>
          </div>
        ) : event && packageData ? (
          <div className="ticket-details-layout">
            <div className="ticket-details-left-column">
              <EventSummaryCard event={event} />
              <PackageDetailsCard
                event={event}
                packageData={packageData}
                attendeeCount={delegates.length}
                canDecrement={delegates.length > 1}
                onIncrement={handleAddRow}
                onDecrement={handleRemoveRow}
                onPurchaseClick={handleOpenForm}
                onAttendeeCountChange={handleAttendeeCountChange}
              />
              <TermsCard terms={event.terms_and_conditions} />
              <div className="ticket-purchase-button-container">
                <button
                  type="button"
                  className="ticket-purchase-button ticket-purchase-button--small"
                  onClick={handleOpenForm}
                >
                  Purchase Tickets
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="ticket-details-error">
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⚠️</div>
            <div>No event or package data available</div>
          </div>
        )}

        {showFormModal && (
          <div className="ticket-modal">
            <div className="ticket-modal__backdrop" onClick={handleCloseForm} />
            <div
              className={`ticket-modal__content ticket-modal__content--${Math.min(
                delegates.length,
                4
              )}`}
            >
              <button
                type="button"
                className="ticket-modal__close-btn"
                onClick={handleCloseForm}
              >
                ×
              </button>
              <AttendeeFormCard
                delegates={delegates}
                attendeeTypes={attendeeTypes}
                titles={titles}
                genders={genders}
                validationErrors={validationErrors}
                onFieldChange={handleInputChange}
                onSubmit={() => {
                  const isValid = handleSubmit();
                  if (isValid) {
                    setShowFormModal(false);
                  }
                }}
                packageData={packageData}
                onRemoveDelegate={handleRemoveDelegateAtIndex}
                event={event}
              />
            </div>
          </div>
        )}
      </main>

      {/* Customer Profile Incomplete Popup */}
      {showCustomerPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }}>
            <button 
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#666',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
              }}
              onClick={() => setShowCustomerPopup(false)}
            >
              ×
            </button>
            
            <div style={{
              fontSize: '48px',
              color: '#ffc107',
              marginBottom: '16px',
            }}>
              ⚠️
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#333',
              margin: '0 0 12px 0',
              background: 'linear-gradient(135deg, #ffc107, #ff9800)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Profile Incomplete
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.5',
              margin: '0 0 20px 0',
            }}>
              {customerMessage}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTicketDetails;
